import { IS_CLIENT, IS_PRODUCTION, sha256Hash } from '@repo/common-utils';
import mixpanel, { Config } from 'mixpanel-browser';

import { MixpanelInitializeProps, SendEventProperties, SetUserParams, EventTrackingInitializeProps } from '../types';

import { BaseService } from './BaseService';

export class MixpanelService extends BaseService {
  private readonly mixpanelKey?: string;
  private readonly mixpanelConfig?: Partial<Config>;
  private readonly landingId?: string;
  private isInit: boolean = false;
  private canInitializeMixpanel: boolean = false;
  private preservedEvents: { eventName: string; properties?: SendEventProperties }[] = [];

  constructor({ appId, clientId, mixpanelKey, mixpanelConfig, landingId, canInitializeMixpanel }: EventTrackingInitializeProps & MixpanelInitializeProps) {
    super(appId, clientId);
    this.mixpanelKey = mixpanelKey;
    this.landingId = landingId;
    this.canInitializeMixpanel = canInitializeMixpanel ?? false;
    this.mixpanelConfig = {
      debug: !IS_PRODUCTION && process.env.NEXT_PUBLIC_TRACKING_DEBUG_MODE === 'true',
      track_pageview: false,
      persistence: 'cookie',
      cross_subdomain_cookie: true,
      ...mixpanelConfig
    };
    this.init();
  }

  protected init() {
    if (!IS_CLIENT || !this.mixpanelKey) return;
    if (!this.canInitializeMixpanel) return;

    mixpanel.init(this.mixpanelKey, this.mixpanelConfig);
    const attributes = {
      ...this.commonAttributes,
      ...(this.landingId ? { landing_id: this.landingId } : { landing_id: null })
    };
    mixpanel.register(attributes);
    this.isInit = true;

    this.preservedEvents.forEach(({ eventName, properties }) => this.sendEvent(eventName, properties));
    this.preservedEvents = [];
  }

  get mixpanelDeviceId(): string {
    if (this.isInit) {
      return mixpanel.get_property('$device_id');
    }
    return '';
  }

  setCanInitializeMixpanel(canInitializeMixpanel: boolean) {
    this.canInitializeMixpanel = canInitializeMixpanel;
    if (this.canInitializeMixpanel && !this.isInit) {
      this.init();
    }
  }

  async setUser({ id = '', name = '', phone = '', email = '' }: SetUserParams) {
    try {
      if (this.isInit) {
        mixpanel.identify(id);
        mixpanel.people.union({
          user_id: id,
          name_hash: await sha256Hash(name || ''),
          phone_hash: await sha256Hash(phone || ''),
          email_hash: await sha256Hash(email || '')
        });
      }
    } catch (err) {
      console.info('mixpanel set user error', err);
      throw err;
    }
  }

  sendEvent(eventName: string, properties?: SendEventProperties) {
    if (!this.isInit) {
      this.preservedEvents.push({ eventName, properties });
      return;
    }

    const mergedProperties = {
      ...this.commonAttributes,
      ...(this.landingId && { landing_id: this.landingId }),
      ...properties,
      is_server: false
    };
    mixpanel.track(eventName, mergedProperties, {
      send_immediately: true
    });

    return {
      config: {
        distinct_id: mixpanel.get_distinct_id(),
        ...this.mixpanelConfig
      },
      event: {
        name: eventName,
        ...properties,
        ...this.commonAttributes,
        ...(this.landingId && { landing_id: this.landingId }),
        is_server: false
      }
    };
  }

  unsetUser(): void {
    mixpanel.reset();
  }
}
